import { useEffect, useState } from "react";
import { api } from "../api";
import ConfigurableDrawer from "./ConfigurableDrawer";
import { Badge, Btn, dt, eur } from "./common";
import { toast } from "sonner";
import { FileText, PenLine, Copy, Play, RefreshCw, Sparkles } from "lucide-react";

const FIELD_DEFS = [
  { key: "numero", label: "Numero", render: (c) => <span className="font-mono text-navy">{c.numero || "-"}</span> },
  { key: "titulo", label: "Titulo", render: (c) => c.titulo || "-" },
  { key: "account_nome", label: "Cliente", render: (c) => c.account_nome || "-" },
  { key: "estado", label: "Estado", render: (c) => <Badge value={c.estado} /> },
  { key: "total", label: "Valor", render: (c) => eur(c.total || 0) },
  { key: "inicio", label: "Inicio", render: (c) => c.inicio ? dt(c.inicio) : "-" },
  { key: "fim", label: "Fim / Renovacao", render: (c) => c.fim ? dt(c.fim) : "-" },
  { key: "duracao_meses", label: "Duracao", render: (c) => c.duracao_meses ? `${c.duracao_meses} meses` : "-" },
  { key: "proposta_numero", label: "Proposta origem", render: (c) => c.proposta_numero || c.proposta_id || "-" },
  { key: "criado_em", label: "Criado", render: (c) => c.criado_em ? dt(c.criado_em) : "-" },
];

const DEFAULT_ACTIONS = ["pdf", "copiar_numero", "assinatura", "ativar", "renovar", "renewal_wizard"];
const DEFAULT_ACTIONS_VISIBLE = { pdf: true, copiar_numero: true, assinatura: true, ativar: true, renovar: true, renewal_wizard: true };

function makeActionDefs({ onOpenPdf, onOpenSign, onActivate, onRenew, onRenewalWizard }) {
  return [
    { key: "pdf", label: "Ver PDF", icon: FileText, enabled: (c) => !!c.id,
      run: (c) => onOpenPdf && onOpenPdf(c) },
    { key: "copiar_numero", label: "Copiar numero", icon: Copy, enabled: (c) => !!c.numero,
      run: async (c) => {
        try { await navigator.clipboard.writeText(c.numero); toast.success(`${c.numero} copiado.`); }
        catch { toast.error("Nao foi possivel copiar."); }
      } },
    { key: "assinatura", label: "Assinatura", icon: PenLine,
      enabled: (c) => c.estado !== "assinada" && c.estado !== "ativo" && !!c.id,
      run: (c) => onOpenSign && onOpenSign(c) },
    { key: "ativar", label: "Ativar", icon: Play,
      enabled: (c) => c.estado === "assinada" && !!c.id,
      run: (c) => onActivate && onActivate(c) },
    { key: "renovar", label: "Renovar (+12m)", icon: RefreshCw,
      enabled: (c) => c.estado === "ativo" && !!c.id,
      run: (c) => onRenew && onRenew(c) },
    { key: "renewal_wizard", label: "Gerar renovacao (proposta)", icon: Sparkles,
      enabled: (c) => c.estado === "ativo" && !!c.id,
      run: (c) => onRenewalWizard && onRenewalWizard(c) },
  ];
}

export default function ContractDrawer({ contract, open, onClose, accounts = [], onOpenPdf, onOpenSign, onActivate, onRenew, onRenewalWizard }) {
  const [detail, setDetail] = useState(contract || null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !contract?.id) return;
    let cancel = false;
    setLoading(true);
    api.get(`/cpq/contracts/${contract.id}`).then((r) => {
      if (cancel) return;
      setDetail(r.data);
      setItems(r.data.itens || []);
    }).catch(() => {
      if (!cancel) { setDetail(contract); setItems(contract.itens || []); }
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [open, contract?.id]); // eslint-disable-line

  const c = detail || contract || {};
  const enriched = { ...c, account_nome: accounts.find((a) => a.id === c.account_id)?.nome || c.account_nome || "-" };
  const placeholders = {
    id: c.id || "", numero: c.numero || "", titulo: c.titulo || "",
    cliente: enriched.account_nome || "", estado: c.estado || "",
    total: String(c.total || 0), inicio: c.inicio || "", fim: c.fim || "",
    duracao_meses: String(c.duracao_meses || ""), account_id: c.account_id || "",
    proposta_id: c.proposta_id || "", proposta_numero: c.proposta_numero || "",
  };

  const extraSections = [];
  if (items.length > 0) {
    extraSections.push({
      key: "itens", label: `Linhas (${items.length})`, defaultVisible: true,
      render: () => (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5">
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-navy truncate">{it.descricao || "-"}</div>
                <div className="text-[10px] text-slate-500">Qtd {it.quantidade || 0} · {eur(it.preco_unitario || 0)} · IVA {it.iva_pct || 0}%</div>
              </div>
              <div className="text-[12px] font-semibold text-navy">
                {eur(((it.quantidade || 0) * (it.preco_unitario || 0)) * (1 - (it.desconto_pct || 0) / 100))}
              </div>
            </div>
          ))}
        </div>
      ),
    });
  }
  if ((c.adendas || []).length > 0) {
    extraSections.push({
      key: "adendas", label: `Adendas (${c.adendas.length})`, defaultVisible: true,
      render: () => (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {c.adendas.map((ad, i) => (
            <div key={i} className="text-[12px] text-navy bg-slate-50 rounded px-2 py-1.5">
              <div className="font-medium">{ad.titulo || `Adenda #${i + 1}`}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{ad.em ? dt(ad.em) : ""} - {ad.autor || ""}</div>
              {ad.descricao && <div className="text-[11px] mt-1">{ad.descricao}</div>}
            </div>
          ))}
        </div>
      ),
    });
  }

  return (
    <ConfigurableDrawer
      open={open} onClose={onClose}
      overline="CPQ"
      title={c.titulo || (loading ? "A carregar..." : "Contrato")}
      testid="contract-drawer"
      uiPrefKey="contract_drawer" uiPrefVersion="v1"
      fieldDefs={FIELD_DEFS}
      actionDefs={makeActionDefs({ onOpenPdf, onOpenSign, onActivate, onRenew, onRenewalWizard })}
      defaultActions={DEFAULT_ACTIONS}
      defaultActionsVisible={DEFAULT_ACTIONS_VISIBLE}
      extraSections={extraSections}
      entity={enriched}
      entityLoading={loading && !detail}
      placeholders={placeholders}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} testid="contract-drawer-close-btn">Fechar</Btn>
          {onOpenPdf && <Btn onClick={() => onOpenPdf(c)} testid="contract-drawer-pdf-btn"><FileText size={14} /> PDF</Btn>}
        </>
      }
    />
  );
}
